import { createContext, ReactNode, useCallback, useMemo } from 'react';
import { useAppSelector } from '@/redux/hooks';
import { paymentSelectors } from '@/redux/selectors';
import { AssetsEnum } from '@/config';

export interface PaymentContextProps {
  getBestDiscount: (tariffCost: number, userDiscount: number) => number;
  isCardPaymentAvailable: (tariffCost: number) => boolean;
  isDiscountWithExpirationTime: () => boolean;
}

export const PaymentContext = createContext<PaymentContextProps | undefined>(
  undefined,
);

type PaymentProviderProps = {
  children: ReactNode;
};

const PAYMENT_ASSET = AssetsEnum.USD;

export const PaymentProvider = ({ children }: PaymentProviderProps) => {
  const { paymentPlans, discountDueTime } = useAppSelector(
    paymentSelectors.getPaymentSlice,
  );

  const getBestDiscount = useCallback(
    (tariffCost: number, userDiscount: number) => {
      const minAmount = paymentPlans?.assetsMinAmounts?.[PAYMENT_ASSET];
      if (minAmount !== undefined && minAmount > tariffCost) {
        return userDiscount;
      }

      const assetDiscount =
        Number(paymentPlans?.assetsDiscounts?.[PAYMENT_ASSET]) || 0;

      return Math.max(userDiscount, assetDiscount);
    },
    [paymentPlans],
  );

  const isCardPaymentAvailable = useCallback(
    (tariffCost: number) => {
      if (!paymentPlans) return false;

      const isAllowed = Object.values(
        paymentPlans.allowedAssets ?? {},
      ).includes(PAYMENT_ASSET);
      if (!isAllowed) return false;

      const minAmount = paymentPlans.assetsMinAmounts?.[PAYMENT_ASSET];
      return minAmount === undefined || minAmount <= tariffCost;
    },
    [paymentPlans],
  );

  const isDiscountWithExpirationTime = useCallback(() => {
    return discountDueTime
      ? discountDueTime > Math.round(new Date().getTime() / 1000)
      : false;
  }, [discountDueTime]);

  const value = useMemo(
    () => ({
      getBestDiscount,
      isCardPaymentAvailable,
      isDiscountWithExpirationTime,
    }),
    [getBestDiscount, isCardPaymentAvailable, isDiscountWithExpirationTime],
  );

  return (
    <PaymentContext.Provider value={value}>{children}</PaymentContext.Provider>
  );
};
